// Composants présentiels de l'Observatoire — zéro emoji (icônes trait lucide, DA CDC-05).
import { useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import axios from 'axios'
import { ChevronDown, Clock, Database, Lock, Pin } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { useBaseObs } from './baseObs'
import ChartBlock, { type ChartSpec as SpecChartBlock } from '../observatoire/ChartBlock'
import TableauEtat from './TableauEtat'
import ResidenceCrous from './ResidenceCrous'
import { NOM_PRODUIT } from '../constants'
import { DEFS } from './defs'
import { Explicable } from './Explication'
import { FicheBtn } from './FicheTechnique'
import { DateLecture, LectureTexte } from './LectureTerobs'
import { cleRepliTuile, cleTailleGraphique, ComparaisonsRepliablesCtx, TaillesGraphiquesCtx,
         type Pin as PrefPin, type PrefsApi } from './prefs'
import { estMasqueCtx } from './masques'
import { BandeauMasque, MasquerBtn } from './MasquerTuile'
import { AvisRepli, BoutonRepli, type Coin } from './RepliComparaisons'
import { axeTerritoires, kpiAComparaisons } from './repliComparaisons'
import { BoutonTaille } from './TailleGraphique'
import { espaceAvantUnite, fmt, fmtDelta, uniteComparaison } from './format'
import { construireComparaison } from './comparaison'
import BoutonPartage, { type PartageMeta } from './partage/BoutonPartage'
import { composerGraphique, composerTuile } from './partage/image'
import { creerLien } from './partage/lien'
import { BoutonAgrandir, CadrePleinEcran } from './PleinEcran'
import { usePleinEcran } from './usePleinEcran'
import { estRoutePrint } from './tableau/BasculeVue'
import type { CommentaireLlm, EchelleKpi, Kpi, SectionChart, SourceDetail, TemoinKpi } from './types'

// Contexte d'épinglage transmis par l'écran (chantier Van 10/07) : absent = pas de pin
// (visiteur anonyme, print, sections gated). t/s identifient l'emplacement de la donnée.
export interface PinCtx {
  t: string
  s: string
  api: Pick<PrefsApi, 'estEpingle' | 'togglePin'> & Partial<Pick<PrefsApi, 'estMasque' | 'basculerMasque'>>
  /** P12 : « Afficher les éléments masqués » est actif — les éléments masqués restent rendus,
      grisés, avec « Afficher de nouveau ». Faux : ils ne sont pas rendus du tout. */
  afficherMasques?: boolean
}

function PinBtn({ ctx, k, c }: { ctx: PinCtx; k: string; c?: boolean }) {
  const pin: PrefPin = { t: ctx.t, s: ctx.s, k, c: !!c }
  const actif = ctx.api.estEpingle(pin)
  return (
    <button type="button" className={`obs2-pin${actif ? ' active' : ''}`}
      aria-label={actif ? `Retirer « ${k} » de mes favoris` : `Ajouter « ${k} » à mes favoris`}
      aria-pressed={actif}
      title={actif ? 'Retirer de mes favoris' : 'Ajouter à mes favoris (vue d\'ensemble)'}
      onClick={e => { e.preventDefault(); e.stopPropagation(); ctx.api.togglePin(pin) }}>
      <Pin size={13} aria-hidden="true" />
    </button>
  )
}

// `fmt`, `fmtDelta` et `uniteComparaison` vivent désormais dans `./format` (22/09) et ne
// sont que ré-exportés ici : le partage en image doit écrire EXACTEMENT les mêmes nombres
// que l'écran, et les lui importer depuis `components.tsx` formait un cycle entre les deux
// modules. Les appels existants (synthèse, tableau des écarts, tests) sont inchangés.
export { fmt, uniteComparaison }

// Info-bulle de définition (retour Marc 06/07) : DÉFINIT l'indicateur (DEFS.short),
// source en ligne discrète. Jamais `interpretation`/`full` (chiffres Saint-Denis maquette).
// Focusable au clavier (tabIndex) ; le CSS .obs2-def gère :hover ET :focus.
export function DefTerm({ term, children }: { term: string; children: ReactNode }) {
  const d = DEFS[term]
  if (!d) return <>{children}</>
  return (
    <span className="obs2-def" tabIndex={0}>
      <span className="obs2-def-term">{children}</span>
      <span className="obs2-def-tip" role="tooltip">
        <span className="obs2-def-tip-text">{d.short}</span>
        {d.source ? <span className="obs2-def-tip-src">Source : {d.source}</span> : null}
      </span>
    </span>
  )
}

// `sources` (lot P, 15/09) : les sources officielles de la SECTION, descendues jusqu'à la
// tuile. Elles sont le DERNIER recours de la rubrique « D'où vient le chiffre » — quand ni
// fiche ni définition n'existent, le panneau dit au moins de quelles bases la section est
// faite, plutôt que de rester muet.
// `explicable={false}` : le PDF. Une tuile imprimée ne s'ouvre pas, et lui coller un
// `role="button"` décrirait au lecteur d'écran d'un document papier une action impossible.
// `partage` (lot du 22/09) : présent = la tuile porte un bouton « Partager » (PNG, et SVG
// depuis le 30/09). Absent, rien ne change — le PDF (`PrintView`) rend les mêmes tuiles et
// ne doit pas hériter d'un bouton d'action. La vue EPCI, elle, le reçoit (30/09).
// `outil` (P10, 12/10) : un contrôle rendu au pied de CHAQUE tuile — à la vue EPCI, le menu
// « Communes membres ». Absent (vue commune, PDF), rien ne change.
// `espaceRepli` (14/10, Olivier) : où ranger le repli des comparaisons de ces tuiles — l'id de la
// section (commune comme EPCI), « synthese » pour les grands chiffres. Absent (PDF, lien partagé),
// aucune tuile ne se replie.
export function KpiCards({ items, pinCtx, sources, explicable = true, partage, outil, espaceRepli }:
                         { items: Kpi[]; pinCtx?: PinCtx; sources?: SourceDetail[]
                           explicable?: boolean; partage?: PartageMeta
                           outil?: (k: Kpi) => ReactNode; espaceRepli?: string }) {
  return (
    <div className="obs2-kpis">
      {items.map((k, i) => !k.gated && estMasqueCtx(pinCtx, k.label) && !pinCtx?.afficherMasques
        ? null : k.gated ? (
        <div className="obs2-tuile-gated" key={i}>
          <div className="obs2-kpi-label">{k.label}</div>
          <div className="lock" aria-label="Réservé aux abonnés"><Lock size={17} /></div>
        </div>
      ) : (
        // Composant à part (et non une branche du `map`) : la contrainte d'origine — un
        // hook ne se déclare pas dans une boucle — est tombée avec la bascule de tuile
        // (W1), le découpage reste parce qu'il rend la tuile testable isolément.
        <KpiCard k={k} pinCtx={pinCtx} sources={sources} explicable={explicable}
                 partage={partage} outil={outil} espaceRepli={espaceRepli} key={i} />
      ))}
    </div>
  )
}

// W1 (réunion du 13/09) — LA BASCULE « Graphique | Tableau » N'EST PLUS SUR LES TUILES.
//
// Livrée sur les tuiles le 12/09 (C1), elle a été retirée au premier regard de Marc :
// « quand je suis dans la vue d'ensemble et que j'ai des tuiles, il y a marqué graphique
// et tableau ALORS QUE MA TUILE N'EST PAS UN GRAPHIQUE ». Cyrille : « on supprime donc le
// bouton graphique tableau et on garde la vue graphique qui correspond à la vue
// graphique ». Le mot « Graphique » posé sur un chiffre unique promet une représentation
// qui n'existe pas, et l'onglet actif l'affirme sur chacune des quinze tuiles d'une page.
//
// ⚠️ CE RETRAIT NE COÛTE AUCUNE ACCESSIBILITÉ, et c'est vérifiable : la bascule a été
// livrée le 12/09 pour donner à un GRAPHIQUE son alternative textuelle (RGAA 1.1 / 1.3 —
// un canvas n'est pas lisible par un lecteur d'écran). Une tuile, elle, n'a jamais eu ce
// problème : sa valeur, ses lignes de comparaison et ses témoins sont déjà du TEXTE dans
// le DOM (`ComparaisonKpi` / `TemoinsKpi`, rendus ci-dessous et inchangés). Le tableau de
// tuile n'était donc pas une alternative à un contenu non textuel : c'était une seconde
// présentation du même texte. La bascule reste, telle quelle, sur les vrais graphiques
// (`ChartBlock`), où elle porte toujours l'alternative.
// LOT P (15/09) — LA TUILE ENTIÈRE EST LE POINT D'ENTRÉE PÉDAGOGIQUE.
//
// Le ⓘ de 18 px a disparu d'ici. Il n'est pas supprimé du produit (le bandeau
// « Périmètre de la commune » le garde, faute d'objet cliquable à offrir) : il est
// REMPLACÉ, sur les tuiles, par le clic sur la tuile — demande de Marc, « quand on clique
// sur les tuiles ou les graphiques, que ça fasse une explication de la donnée ».
//
// Ce retrait GAGNE de l'accessibilité au lieu d'en coûter, et c'est mesurable : la cible
// passe de 18 px (sous le minimum de 24 px du critère WCAG 2.5.8) à la tuile entière,
// jamais moins de 250 px de large. Le panneau ouvert dit strictement plus que la modale
// qu'il remplace : il ajoute la définition longue, les notes de méthode des échelles et
// les sources de la section, que le ⓘ n'a jamais montrées.
//
// L'infobulle de survol reste — c'est l'APERÇU. Elle répond en une phrase sans rien
// ouvrir ; le panneau répond en détail pour qui le demande.
//
// 30/09 (Marc) — le déclencheur se RESSERRE : la tuile entière ouvrait le panneau à chaque
// clic de lecture. Seul le bouton (i) « Comprendre ce chiffre », en bas de la tuile, l'ouvre
// désormais (voir `Explicable`) : un vrai bouton de 24 px au moins, nommé par l'indicateur.
// 14/10 (Olivier, Grand Besançon) : « Masquer les comparaisons » — la tuile ne garde que la
// valeur du territoire consulté ; échelles et témoins reviennent d'un clic. Choix gardé au compte.
function KpiCard({ k, pinCtx, sources, explicable = true, partage, outil, espaceRepli }:
                 { k: Kpi; pinCtx?: PinCtx; sources?: SourceDetail[]; explicable?: boolean
                   partage?: PartageMeta; outil?: (k: Kpi) => ReactNode; espaceRepli?: string }) {
  const replis = useContext(ComparaisonsRepliablesCtx)
  const masquee = estMasqueCtx(pinCtx, k.label)
  const partageable = !!partage && k.value !== null && k.value !== undefined
  const compare = k.secondaire ?? k
  // Les commandes du coin, chacune à sa place (voir ChartCard) : le libellé réserve la place
  // jusqu'à la plus à gauche, le bouton de repli prend la suivante.
  const coin = pinCtx?.api.basculerMasque && !masquee ? 3 : partageable ? 2 : pinCtx ? 1 : 0
  const cleRepli = replis && espaceRepli && !estRoutePrint() && kpiAComparaisons(k)
    ? cleRepliTuile(espaceRepli, k.label) : null
  const replie = !!cleRepli && !!replis?.estReplie(cleRepli)
  const basculerRepli = () => { if (cleRepli) replis?.basculerRepli(cleRepli) }
  const reserve = cleRepli ? coin + 1 : coin
  return (
    <Explicable className={masquee ? 'obs2-kpi obs2-element-masque' : 'obs2-kpi'} actif={explicable} cible={{
      // #563 : le libellé est l'ancre précise de la tuile (« Commenter », lien profond).
      titre: k.label, tuile: k.label, defTerm: k.def_term, fiche: k.fiche, note: k.note, sources,
      notesMethode: (compare.comparaison ?? [])
        .map(e => e.note_methode).filter((n): n is string => !!n),
    }}>
      {pinCtx ? <PinBtn ctx={pinCtx} k={k.label} /> : null}
      {pinCtx ? <MasquerBtn ctx={pinCtx} k={k.label} /> : null}
      {cleRepli ? <BoutonRepli nom={k.label} replie={replie} coin={(coin + 1) as Coin}
                               onBasculer={basculerRepli} /> : null}
      {pinCtx && masquee ? <BandeauMasque ctx={pinCtx} k={k.label} /> : null}
      {/* Pas de bouton sur un « N/D » : une image dont la valeur est « donnée non
          disponible » n'apprend rien au destinataire et use la confiance dans ce qu'on
          envoie. Les tuiles verrouillées, elles, ne passent même pas par ici. */}
      {partage && partageable ? (
        <BoutonPartage titre={k.label} partage={partage}
          composer={() => composerTuile(k, partage, sources?.map(s => s.nom))}
          // Le SVG (30/09) : module chargé au premier export seulement.
          composerSvg={async () => (await import('./partage/imageSvg'))
            .composerTuileSvg(k, partage, sources?.map(s => s.nom))} />
      ) : null}
      <div className={reserve ? `obs2-kpi-label obs2-kpi-label--coin-${reserve}` : 'obs2-kpi-label'}>
        {k.def_term ? <DefTerm term={k.def_term}>{k.label}</DefTerm> : k.label}
      </div>
      <div className="obs2-kpi-value">
        {k.value === null || k.value === undefined
          ? <span className="obs2-nd">N/D</span>
          : <>{fmt(k.value, k.unit, k.decimales)}{k.unit ? <span className="obs2-kpi-unit">{k.unit}</span> : null}</>}
      </div>
      {k.secondaire && k.annee != null && <div className="obs2-kpi-annee">{k.annee}</div>}
      {k.secondaire && <div className="obs2-kpi-note">
        {k.secondaire.value == null ? 'N/D' : fmt(k.secondaire.value, k.secondaire.unit, 1)}
        {' '}{k.secondaire.unit}
      </div>}
      {/* Badge « Certifié » déposé (Marc 13/07) : omniprésent il ne disait plus rien,
          et son absence accidentelle devenait un aveu. La fiche technique cliquable
          le remplace — la preuve à la demande plutôt que le tampon partout. */}
      {k.note && <div className="obs2-kpi-note">{k.note}</div>}
      {k.limite && <div className="obs2-kpi-note obs2-kpi-limite">{k.limite}</div>}
      {k.parc_crous && <div className="obs2-crous">
        {k.parc_crous.residences.length === 0
          ? <p>Aucune résidence du CROUS recensée.</p>
          : <details open={k.parc_crous.residences.length <= 6}>
              <summary>Liste des résidences</summary>
              <ul aria-label="Résidences du CROUS">
                {k.parc_crous.residences.map((r, i) => <li key={r.id ?? `${r.nom}-${i}`}>
                  <ResidenceCrous residence={r} />
                </li>)}
              </ul>
            </details>}
      </div>}
      {/* Repliée : ni échelles ni témoins à l'écran — l'image partagée, le PDF et l'export les
          gardent (ils lisent la tuile servie, pas cet affichage). */}
      {/* L'impression du navigateur (Ctrl+P) les garde aussi (revue codex, 3e passe) : rendues
          dans un bloc réservé au papier (`@media print`, repli.css), absent de l'écran. */}
      {replie ? <>
        <AvisRepli onAfficher={basculerRepli} />
        <div className="obs2-repli-impression">
          {compare.comparaison && <ComparaisonKpi echelles={compare.comparaison} unite={compare.unit} />}
          {compare.temoins && <TemoinsKpi temoins={compare.temoins} unite={compare.unit} />}
        </div>
      </> : <>
        {compare.comparaison && <ComparaisonKpi echelles={compare.comparaison} unite={compare.unit} />}
        {compare.temoins && <TemoinsKpi temoins={compare.temoins} unite={compare.unit} />}
      </>}
      {outil?.(k)}
    </Explicable>
  )
}


// `uniteComparaison` est parti dans `./format` et la construction des lignes de
// comparaison (avec ses phrases de méthode génériques) dans `./comparaison` : l'image
// partagée les appelle aussi, et deux copies de la règle auraient divergé — elles
// AVAIENT divergé, c'est ce que la revue croisée du 22/09 a trouvé.

// A2 — la méthodologie des échelles, écrite UNE fois par page.
// Elle remplace le paragraphe qui était répété sous chacune des quinze tuiles. Ce qui ne
// peut PAS remonter ici et reste par valeur : quelles échelles viennent du producteur
// (marquées « publié » sur leur ligne) et quelles couvertures sont partielles — sans quoi
// on perdrait l'attribution par valeur exigée le 02/08, qui est la raison d'être du bloc.
// ⚠️ `variante="texte"` pour le PDF (revue codex P2) : `ComparaisonKpi` est PARTAGÉE avec
// `PrintView`. Retirer la méthodologie des tuiles sans la rendre au document imprimé aurait
// laissé les études exportées sans aucune légende — et le PDF ne peut pas consulter
// l'infobulle du marqueur « publié ». Le document qui circule en réunion est précisément
// celui où une valeur non expliquée coûte le plus cher.
export function NoteMethodeEchelles({ variante = 'bandeau' }: { variante?: 'bandeau' | 'texte' }) {
  const texte = (
    <>
      <strong>D'où viennent les valeurs de comparaison ?</strong> Les échelles marquées
      {' '}<em className="obs2-temoin-tag">·&nbsp;publié</em> sont les agrégats du
      producteur lui-même (INSEE, DGCL, SSMSI) pour ce périmètre — aucune reconstitution de
      notre part. Toutes les autres sont calculées par nos soins, commune par commune, à
      partir des sources de l'indicateur — et non reprises des publications du producteur à
      ces échelles. Sauf mention contraire à côté d'un périmètre, elles sont renseignées en
      totalité ; une couverture partielle ou non mesurée est toujours écrite sur la ligne
      concernée. Lorsque le producteur ne publie pas de valeur France — cas de la
      sécurité —, notre ligne France est la somme de ses publications départementales
      (France métropolitaine), recoupée contre un second chemin de calcul quand il en
      existe un.
    </>
  )
  return variante === 'texte'
    ? <p className="print-methodo">{texte}</p>
    : <div className="obs2-banner" style={{ marginTop: 18 }}>{texte}</div>
}

// Lot A (CDC retours du 08/10 au soir, #622). Marc, tuile fibre de Besançon : « 98,5 » puis
// « % » SEUL à la ligne, sous un libellé long (« Bourgogne-Franche-Comté · couverture :
// 99,9 % »). La règle A12 du 12/09 rendait toute unité repliable, pour que « pour 1 000 hab.
// de 75 ans et plus » ne déborde plus ; elle laissait aussi partir le « % ». La coupure se
// décide désormais par LONGUEUR d'unité : une unité courte (« % », « €/hab », « ‰ », « km »)
// est collée à son nombre par une espace insécable — et sa classe `courte` la tient sur la
// ligne même si le texte venait à changer ; une unité longue garde la règle du 12/09.
// Seuil de 12 (CDC, lot A) : il couvre avec de la marge les unités courtes du registre (75
// « % », 15 « €/hab ») et laisse à la règle du 12/09 « pour 1 000 hab. » (15 caractères),
// une unité assez longue pour passer à la ligne sans se lire faux.
// La règle elle-même vit dans `./format` depuis le 11/10 : l'image d'une tuile écrit ses
// témoins avec la même espace que l'écran, et `partage/image.ts` ne peut pas importer ce
// fichier (cycle, voir l'en-tête de `format.ts`). Les appelants l'importent de `./format`.
const NBSP = '\u00A0'

/** L'unité d'une ligne de comparaison ou de témoin, collée ou repliable selon sa longueur. */
function UniteLigne({ unite }: { unite: string }) {
  const sep = espaceAvantUnite(unite)
  return (
    <span className={sep === NBSP ? 'obs2-kpi-comp-u courte' : 'obs2-kpi-comp-u'}>
      {sep}{unite}
    </span>)
}

// Le CALCUL des lignes vit dans `./comparaison` depuis le 22/09 — l'image partagée doit
// écrire exactement les mêmes nombres que l'écran, et elle en avait écrit sa propre
// version (totaux de périmètre au lieu des parts, unité de tuile au lieu de l'unité de
// ligne, écarts non signés). Ici, il ne reste que la mise en forme.
function ComparaisonKpi({ echelles, unite: uniteKpi }: { echelles: EchelleKpi[]; unite?: string }) {
  const comp = construireComparaison(echelles, uniteKpi)
  if (!comp) return null

  return (
    <div className="obs2-kpi-comp">
      {comp.lignes.map(l => (
        <div key={l.cle} className="obs2-kpi-comp-l">
          {/* Le libellé porte le NOM réel du périmètre (« Essonne ») — le rôle générique
              (« Son département ») passe en infobulle : la pédagogie reste à un survol,
              sans bruit à l'écran (Marc 06/08). */}
          <span title={l.role}>
            {l.libelle}
            {/* A2 — l'attribution reste PAR VALEUR, mais tient en un mot au lieu d'un
                paragraphe. Sans ce marqueur, hisser la méthodologie en pied de page ferait
                perdre au lecteur la seule chose qu'une note générale ne peut pas dire :
                LAQUELLE de ces quatre lignes vient du producteur. C'est exactement ce que
                l'arbitrage du 02/08 demandait de ne jamais perdre. */}
            {l.publiee && (
              <em className="obs2-temoin-tag"
                  title={`Agrégat calculé et publié par ${l.producteur} pour ce périmètre`}>
                {' '}·&nbsp;publié
              </em>)}
            {l.couverturePartielle != null && (
              <span className="obs2-kpi-comp-couv"
                    title={`Agrégat calculé sur ${l.couverturePartielle} % des communes de `
                         + `ce périmètre — les autres ne renseignent pas cet indicateur`}>
                {/* « couverture » explicite (recette Marc 04/08 : « 92,6 % des communes »
                    se lisait comme une statistique de l'indicateur), mais COURT (recette
                    05/08, décalage.png : la phrase en toutes lettres wrappait sous chaque
                    libellé et cassait les colonnes). Le tooltip garde la phrase complète. */}
                {/* Lot A (08/10) : ce « % » aussi est collé — le libellé se replie, et
                    c'est justement là qu'il partait seul. */}
                {' '}· couverture : {l.couverturePartielle.toLocaleString('fr-FR')}{NBSP}%
              </span>)}
            {/* Mention propre à l'échelle (24/08) : ce qui VARIE d'une ligne à l'autre se
                rend ici, à côté de sa valeur, pendant que la phrase de méthode invariante
                se dédoublonne au pied du bloc. */}
            {l.mentionLigne && (
              <span className="obs2-kpi-comp-couv">{' '}· {l.mentionLigne}</span>)}
          </span>
          <strong>
            {l.valeur}
            {l.unite ? <UniteLigne unite={l.unite} /> : null}
          </strong>
        </div>
      ))}
      {/* Le pied : la mention de part sur un stock, sinon les notes de méthode propres à
          l'indicateur, dédoublonnées. `construireComparaison` tranche entre les deux. */}
      {comp.notes.map((n, i) => <div key={i} className="obs2-kpi-comp-n">{n}</div>)}
      {/* A2 — la tuile ne dit plus QUE ce que la note de page ne peut pas dire : les
          exceptions propres à CET indicateur. Couverture non mesurée et échelle non
          comparable sont deux faits distincts, et se disent toutes les deux. */}
      {comp.reserve && <div className="obs2-kpi-comp-n">{comp.reserve}</div>}
    </div>
  )
}

// « Les jetons » (Marc, 28/07) : jusqu'à 3 communes choisies par le client, PLUS celles que
// TerObs propose. Rendu sous les échelles, dans la même veine discrète — d'abord son
// territoire, ensuite ses pairs.
//
// L'écart est affiché SANS couleur, délibérément et comme pour les échelles : rien ne dit
// qu'avoir plus de logements vacants ou moins de résidences secondaires soit « bien » ou
// « mal ». Colorer, ce serait juger à la place de l'élu — la doctrine du produit est de
// montrer des états de fait, jamais un classement anxiogène.
function TemoinsKpi({ temoins, unite: uniteKpi }: { temoins: TemoinKpi[]; unite?: string }) {
  // Lot B (CDC du 09/10) : la note mène là où les pairs se lisent et se gèrent — la page
  // Comparaison. Hors d'une page communale (vue EPCI, lien partagé, tests sans routeur), pas
  // de commune à viser : la note reste du texte. (Hook avant tout retour anticipé.)
  const { insee } = useParams<{ insee?: string }>()
  const baseObs = useBaseObs()   // maquettes vivantes de /admin/design (PR #684)
  const unite = uniteComparaison(uniteKpi)
  // CDC « témoins partout » (10/10), étape 8 : un témoin sans valeur reste à sa place et
  // s'écrit « N/D ». Avant, il était retiré de la liste : l'élu qui avait choisi Arpajon ne la
  // retrouvait pas sur la tuile, sans savoir si c'était un oubli, une panne ou un secret
  // statistique. Le standard veut qu'un manque se déclare, jamais qu'il se taise.
  if (!temoins.length) return null
  const proposes = temoins.some(t => t.origine === 'propose')
  const note = 'communes de strate comparable — modifiables depuis la page Comparaison'
  return (
    <div className="obs2-kpi-temoins">
      {temoins.map(t => {
        // Le motif vient du serveur (`raison_nd` : « secret statistique », « millésime
        // absent »…). Au survol pour la souris, en texte masqué pour le lecteur d'écran, qui
        // ne lit pas les `title`. Sans motif, rien : un motif inventé serait pire que l'absence.
        const motif = t.valeur === null ? t.raison_nd?.trim() || null : null
        return (
          <div key={t.code} className="obs2-kpi-comp-l"
               title={motif ? `${t.nom}${NBSP}: N/D, ${motif}` : undefined}>
            <span>
              {t.nom}
              {t.origine === 'propose' && <em className="obs2-temoin-tag" title={`Pair proposé par ${NOM_PRODUIT}`}> ·&nbsp;proposée</em>}
            </span>
            {t.valeur === null ? (
              <strong>
                <span className="obs2-temoin-nd">N/D</span>
                {motif && <span className="sr-only">{` (donnée non disponible${NBSP}: ${motif})`}</span>}
              </strong>
            ) : (
              <strong>
                {fmt(t.valeur, unite)}
                {unite ? <UniteLigne unite={unite} /> : null}
                {t.ecart_commune != null && (
                  <span className="obs2-temoin-ecart">
                    {' '}({fmtDelta(t.ecart_commune, unite)})
                  </span>
                )}
              </strong>
            )}
          </div>
        )
      })}
      {proposes && (
        <div className="obs2-kpi-comp-n">
          {insee ? <Link to={`${baseObs}/o/${insee}/comparaison`}>{note}</Link> : note}
        </div>
      )}
    </div>
  )
}

// A4 : plus de titre interne (la section h3 l'affiche déjà — doublon constaté audit 04/07).
// A8 : le prix du palier de LA commune suit l'utilisateur dans chaque gate.
// A27 : prix toujours en « HT (TTC) ».
export interface PalierInfo { label?: string; prix_annuel_ht: number | null }

// Le taux vient du régime fiscal servi par le serveur, comme pour les devis et factures.
let _tauxTvaCache: number | null = null

/** Taux de TVA applicable (0 sous franchise en base). `null` tant qu'il n'est pas connu. */
export function useTauxTva(): number | null {
  const [taux, setTaux] = useState<number | null>(_tauxTvaCache)
  useEffect(() => {
    if (_tauxTvaCache !== null) return
    axios.get<{ tva: number }>(`${(import.meta.env.VITE_API_URL || '')}/api/pricing`)
      .then(r => { _tauxTvaCache = r.data?.tva ?? 0; setTaux(_tauxTvaCache) })
      // Échec réseau : on retombe sur « pas de TVA affichée » plutôt que d'inventer un taux.
      .catch(() => { _tauxTvaCache = 0; setTaux(0) })
  }, [])
  return taux
}

export function prixHtTtc(ht: number, tauxTva: number | null): string {
  const ttc = tauxTva ? ` (${Math.round(ht * (1 + tauxTva)).toLocaleString('fr-FR')} € TTC)` : ''
  return `${ht.toLocaleString('fr-FR')} € HT${ttc}`
}

export function GateCard({ title, insee, palier, montrerTitre = false, reason }: {
  title: string; insee: string; palier?: PalierInfo; montrerTitre?: boolean; reason?: 'tier' | 'role'
}) {
  const baseObs = useBaseObs()
  const tauxTva = useTauxTva()
  const heights = [58, 82, 45, 94, 69, 52, 86]
  return (
    <div className="obs2-gate">
      <div className="obs2-gate-skeleton" aria-hidden="true">
        <div className="t" />
        <div className="bars">{heights.map((h, i) => <span key={i} style={{ height: `${h}%` }} />)}</div>
      </div>
      <div className="obs2-gate-overlay">
        <div className="obs2-gate-icon" aria-hidden="true"><Lock size={16} /></div>
        {montrerTitre
          ? <div className="obs2-gate-title">{title}</div>
          : <span className="sr-only">{title} — contenu réservé</span>}
        <div className="obs2-gate-sub">
          {reason === 'role'
            ? "Accès non activé pour votre compte — les accès sont gérés par l'administrateur de votre espace commune."
            : `Contenu réservé — benchmarks de strate, séries historiques et analyses ${NOM_PRODUIT}.`}
        </div>
        {reason !== 'role' && palier?.prix_annuel_ht != null && (
          <div className="obs2-gate-prix">
            Votre commune : <strong>{prixHtTtc(palier.prix_annuel_ht, tauxTva)}</strong> /an — tout inclus
          </div>
        )}
        {reason !== 'role' && (
          <div className="obs2-cta-row">
            <Link to={`${baseObs}/o/${insee}/synthese#tarifs`} className="obs2-btn obs2-btn-primary">Découvrir l'abonnement</Link>
            <Link to="/contact" className="obs2-btn obs2-btn-ghost">Demander une démo</Link>
          </div>
        )}
      </div>
    </div>
  )
}

/** Le canvas Chart.js d'une carte de graphique, retrouvé depuis son bouton de partage. */
function toileDe(bouton: HTMLButtonElement): HTMLCanvasElement {
  const toile = bouton.closest('.obs2-card')?.querySelector('canvas')
  if (!(toile instanceof HTMLCanvasElement)) throw new Error('Le graphique n’est pas encore dessiné.')
  return toile
}

// `niveau` (audit 07/09) : à l'écran les sections sont des h2 (sous le h1 de la page), le
// titre d'un graphique est donc un h3 ; dans le PDF (PrintView) les sections sont des h3
// sous le h2 de la thématique, et la carte garde son h4. Même rendu au pixel : la classe
// fixe la typographie, Tailwind neutralise les tailles par défaut des titres.
// Lot P (15/09) : la carte du graphique s'ouvre elle aussi sur « Comprendre ce chiffre »,
// et le ⓘ du titre s'efface devant elle.
//
// 30/09 (Marc) : ni le graphique ni le reste de la carte n'ouvrent plus le panneau — un
// graphique se survole, se pointe, se bascule en tableau, et le titre ou la note se lisent.
// Le seul déclencheur est le bouton (i) « Comprendre ce chiffre » en bas de la carte.
// 03/10 (Marc, CDC E2) : `lien` — le compte peut partager ce graphique par un lien de 14 jours.
// Seul l'identifiant stable du graphique (`block.id`) part au serveur, qui reconstruit le reste ;
// un graphique sans identifiant ne se partage pas par lien.
// 10/10 (Marc) : « Agrandir » — le graphique en plein écran (voir PleinEcran.tsx). Jamais sur
// la route print : le PDF ne clique pas. Le tableau d'état (#604) n'est pas un graphique et
// n'en a pas.
// 11/10 (lot A, Marc : « par utilisateur, par paliers ») : « Taille » — trois largeurs, deux
// hauteurs, mémorisées au compte sous la clé stable du graphique (voir TailleGraphique.tsx).
// Seulement là où ObsLayout sert les préférences d'un compte connecté : ni PDF, ni lien partagé,
// ni EPCI. `largeurFixe` : la carte a déjà toute la ligne (épinglée sur la synthèse) — seule la
// hauteur s'y applique. Le plein écran ignore la taille tant qu'il est ouvert.
export function ChartCard({ block, color, pinCtx, niveau = 'h3', sources, explicable = true,
                            partage, lien, largeurFixe = false }: {
  block: SectionChart; color: string; pinCtx?: PinCtx; niveau?: 'h3' | 'h4'
  sources?: SourceDetail[]; explicable?: boolean; partage?: PartageMeta
  lien?: { insee: string; token: string | null }; largeurFixe?: boolean
}) {
  const graphiqueId = block.id
  const Titre = niveau
  const pleinEcran = usePleinEcran()
  const tailles = useContext(TaillesGraphiquesCtx)
  const replis = useContext(ComparaisonsRepliablesCtx)
  // Tableau d'état (#604) : du texte, pas un canvas — ni Chart.js, ni image partagée (le
  // compositeur photographie la toile du graphique, qu'un tableau n'a pas). L'épingle, le
  // commentaire et le (i) de la carte restent ; chaque ligne a en plus le sien.
  const etat = block.chart?.type === 'etat' ? block.chart : null
  // P12 : masqué ET « afficher les masqués » éteint → rien (après les hooks : ils ne se sautent pas).
  const masquee = !!block.title && estMasqueCtx(pinCtx, block.title, true)
  if (masquee && !pinCtx?.afficherMasques) return null
  const commandesMasque = pinCtx && block.title ? <>
    <MasquerBtn ctx={pinCtx} k={block.title} c />
    {masquee ? <BandeauMasque ctx={pinCtx} k={block.title} c /> : null}
  </> : null
  // Les commandes du coin (épingle, partage, masquer), chacune à sa place fixe depuis le bord
  // droit : le titre réserve la place jusqu'à la plus à gauche de celles qui sont rendues
  // (obs.css, `.obs2-card-title--coin-*`), au lieu de courir dessous (Marc, 13/10).
  const coin = pinCtx && block.title && pinCtx.api.basculerMasque && !masquee ? 3
    : partage && block.chart && !etat ? 2
    : pinCtx && block.title ? 1 : 0
  // 14/10 (Olivier) : « Masquer les comparaisons », sur un graphique qui en porte (échelles,
  // médiane, témoins) — à la place libre à gauche des autres commandes, sous la clé stable du
  // graphique (celle des tailles), mémorisé au compte. Jamais sur la route print : le PDF garde
  // tout. Replié, `ChartBlock` ne trace que le territoire consulté (graphique et tableau).
  const cle = cleTailleGraphique(block)
  const cleRepli = replis && cle && block.title && !etat && !estRoutePrint()
    && axeTerritoires(block.chart as SpecChartBlock | null) !== null ? cle : null
  const replie = !!cleRepli && !!replis?.estReplie(cleRepli)
  const basculerRepli = () => { if (cleRepli) replis?.basculerRepli(cleRepli) }
  const reserve = cleRepli ? coin + 1 : coin
  const commandeRepli = cleRepli
    ? <BoutonRepli nom={block.title} replie={replie} coin={(coin + 1) as Coin} onBasculer={basculerRepli} />
    : null
  const classeTitre = reserve ? `obs2-card-title obs2-card-title--coin-${reserve}` : 'obs2-card-title'
  if (etat) {
    return (
      <Explicable className={masquee ? 'obs2-card obs2-card-etat obs2-element-masque' : 'obs2-card obs2-card-etat'}
        actif={explicable}
        cible={{ titre: block.title, note: block.note, sources, graphique: graphiqueId }}>
        {pinCtx && block.title ? <PinBtn ctx={pinCtx} k={block.title} c /> : null}
        {commandesMasque}
        <Titre className={classeTitre}>{block.title}</Titre>
        <TableauEtat lignes={etat.lignes ?? []} titre={block.title} sources={sources}
          explicable={explicable} />
        {block.note ? <p className="obs2-card-note">{block.note}</p> : null}
      </Explicable>
    )
  }
  const agrandissable = !!block.chart && !estRoutePrint()
  // La pyramide et les barres horizontales ont leur hauteur propre : seule la largeur s'y règle.
  const hauteurPropre = block.chart?.type === 'pyramid' || !!block.chart?.horizontal
  const reglage = agrandissable && tailles && cle && (!largeurFixe || !hauteurPropre)
    ? { tailles, cle } : null
  const memorisee = reglage ? reglage.tailles.tailleDe(reglage.cle) : null
  const appliquee = memorisee && !pleinEcran.ouvert
    ? { l: largeurFixe ? 1 : memorisee.l, h: hauteurPropre ? 's' as const : memorisee.h }
    : null
  const carte = (
    <Explicable className={(appliquee && appliquee.l > 1
      ? `obs2-card obs2-taille-l${appliquee.l}` : 'obs2-card') + (masquee ? ' obs2-element-masque' : '')}
      actif={explicable}
      cible={{ titre: block.title, fiche: block.fiche, note: block.note, sources,
               graphique: graphiqueId }}>
      {pinCtx && block.title ? <PinBtn ctx={pinCtx} k={block.title} c /> : null}
      {commandesMasque}
      {commandeRepli}
      {/* Le canvas de Chart.js se retrouve depuis le bouton (`closest`) plutôt que par une
          ref : `Explicable` ne transmet pas de ref, et lui en ajouter une pour ce seul
          usage toucherait un composant partagé par toutes les tuiles du site.
          Rien à partager si la section n'a pas de graphique (« N/D pour cette commune »). */}
      {partage && block.chart ? (
        <BoutonPartage titre={block.title} partage={partage} composer={bouton => {
          return composerGraphique(block, toileDe(bouton), partage, sources?.map(s => s.nom))
        }} composerSvg={async bouton => {
          // Le SVG est VECTORIEL : le graphique est rejoué, pas photographié (imageSvg.ts).
          const toile = toileDe(bouton)
          const { composerGraphiqueSvg } = await import('./partage/imageSvg')
          return composerGraphiqueSvg(block, toile, partage, sources?.map(s => s.nom))
        }} lien={lien && graphiqueId
          ? { creer: () => creerLien(lien.insee, graphiqueId, lien.token) } : undefined} />
      ) : null}
      <Titre className={classeTitre}>{block.title}</Titre>
      {replie ? <AvisRepli onAfficher={basculerRepli} /> : null}
      {block.chart
        ? <ChartBlock spec={block.chart as SpecChartBlock} color={color} titre={block.title}
            pleinEcran={pleinEcran.ouvert} taille={appliquee} comparaisonsRepliees={replie}
            commandes={agrandissable
              ? <>
                  {reglage && (
                    <BoutonTaille titre={block.title} taille={memorisee}
                      masque={pleinEcran.ouvert} largeurs={!largeurFixe} hauteurs={!hauteurPropre}
                      onChange={t => reglage.tailles.fixerTaille(reglage.cle, t)} />
                  )}
                  <BoutonAgrandir titre={block.title} ouvert={pleinEcran.ouvert}
                    onOuvrir={pleinEcran.ouvrir} refBouton={pleinEcran.bouton} />
                </>
              : undefined} />
        : <p className="obs2-card-note">Donnée non disponible pour cette commune (N/D).</p>}
      {block.note ? <p className="obs2-card-note">{block.note}</p> : null}
    </Explicable>
  )
  if (!agrandissable) return carte
  // Le cadre est TOUJOURS là (transparent au repos) : l'ouvrir ne remonte pas la carte.
  return (
    <CadrePleinEcran ouvert={pleinEcran.ouvert} titre={block.title}
      onFermer={pleinEcran.fermer} retour={pleinEcran.bouton}>
      {carte}
    </CadrePleinEcran>
  )
}

// W3 (réunion du 13/09) — LES PISTES SE LISENT EN PUCES, ICI AUSSI.
//
// Cyrille : « il y a quatre phrases qui se suivent […] ça serait bien pour distinguer les
// différentes phrases et paragraphes qu'il y ait un petit tiret sur les pistes. »
// Marc : « bullet point ».
//
// Le modèle produisait déjà la bonne matière : sur les 459 lectures écrites en production
// le 13/09, 454 portent des lignes ouvertes par « - » (mesuré en lecture seule). Le défaut
// était ici, au RENDU. Ce fichier portait SA propre version du rendu — héritée de P7, quand
// elle était la seule — qui ne découpait QUE sur les lignes vides : le bloc « Pistes : » et
// ses quatre puces tombaient dans un unique <p>. Aucune liste, aucun retrait, quatre phrases
// à la file. `white-space: pre-line` gardait les sauts de ligne, ce qui a longtemps suffi à
// masquer le défaut : les tirets étaient là, alignés sur la marge, noyés dès qu'une piste
// dépassait la ligne — exactement l'aspect que Cyrille décrit.
//
// Le rendu structuré existe déjà, écrit le 12/09 pour l'observatoire d'une commune :
// `LectureTexte` découpe ligne à ligne, construit un vrai <ul>, met en avant l'intitulé
// « Pistes : » et distingue le renvoi « → section … ». On l'appelle, au lieu d'en écrire un
// second : deux rendus du même markdown auraient fini par diverger — ils avaient DÉJÀ
// divergé, et c'est précisément ce que Cyrille a vu.
//
// Ce que cela couvre : l'observatoire INTERCOMMUNAL (EpciObservatoireView) et le PDF
// (PrintView) — celui qui circule en réunion. Aucun appel au modèle, aucune régénération :
// le texte servi est inchangé, seule sa mise en page l'est.
//
// `LlmTexte` reste exporté sous son nom : cinq appels y renvoient, et le renommer en masse
// mêlerait un déplacement de symboles à un correctif d'affichage.
export { LectureTexte as LlmTexte }

// Repliable (Van V14) : l'en-tête toggle, ouvert par défaut, état local (jamais persisté —
// c'est un confort de lecture, pas une préférence).
// A3 (plan 12/09) — « Lecture de l'observatoire » devient « Lecture de TerObs », à l'écran
// comme dans le PDF : le bloc est signé par la marque, pas par un objet générique.
//
// Le libellé était une chaîne LITTÉRALE, et c'était justifié : au 12/09 `NOM_PRODUIT`
// valait encore « LObsTer », donc l'y brancher aurait rendu « Lecture de LObsTer » —
// l'inverse exact de la décision A3. La cohabitation des deux noms était assumée et
// signalée. Elle a cessé le 13/09 avec la bascule de marque : la constante dit
// « TerObs », le littéral n'a plus de raison d'être et le libellé repasse par elle.
export function LlmBlock({ c, label = `Lecture de ${NOM_PRODUIT}` }:
                          { c: CommentaireLlm; label?: string }) {
  const [ouvert, setOuvert] = useState(true)
  return (
    <div className="obs2-llm">
      <button type="button" className="obs2-llm-head" style={{ width: '100%', border: 'none',
          background: 'transparent', cursor: 'pointer', padding: 0, textAlign: 'left' }}
        aria-expanded={ouvert}
        /* label="" (la synthèse a déjà son <h3>) laissait le bouton SANS nom accessible :
           icône et chevron sont aria-hidden, un lecteur d'écran annonçait un bouton vide
           (revue codex 07/08). Le nom retombe sur le libellé par défaut. */
        aria-label={label ? undefined : 'Lecture de TerObs — déplier ou replier'}
        onClick={() => setOuvert(o => !o)}>
        <div className="obs2-llm-icon" aria-hidden="true">L</div>
        {label ? <div className="obs2-llm-label">{label}</div> : null}
        <ChevronDown size={15} aria-hidden="true"
          style={{ marginLeft: 'auto', transition: 'transform .15s',
                   transform: ouvert ? 'none' : 'rotate(-90deg)' }} />
      </button>
      {ouvert && (c.texte
        ? <><LectureTexte texte={c.texte} /><DateLecture c={c} /></>
        : <div className="obs2-llm-pending"><Clock size={15} aria-hidden="true" />
            Analyse rédigée en préparation — elle s'affichera lors d'une prochaine visite.</div>)}
    </div>
  )
}

export function PendingBlock({ sources }: { sources?: string[] }) {
  return (
    <div className="obs2-pending">
      <div className="obs2-pending-title"><Database size={16} aria-hidden="true" /> Données en cours d'ingestion</div>
      <div className="obs2-pending-desc">
        Cette section rejoint l'observatoire lors d'une prochaine mise à jour.
        {sources && sources.length > 0 ? <> Sources attendues : {sources.join(' · ')}.</> : null}
      </div>
    </div>
  )
}

/** Hors du champ d'une source (décision Marc 1, 08/08) : la donnée n'arrivera JAMAIS
    par cette source pour ce territoire — le dire avec le nom de la source, au lieu
    d'une promesse d'ingestion mensongère. */
export function HorsChampBlock({ motif }: { motif?: string | null }) {
  return (
    <div className="obs2-pending">
      <div className="obs2-pending-title"><Database size={16} aria-hidden="true" /> Hors du champ de la source</div>
      <div className="obs2-pending-desc">
        {motif || "Ce territoire n'entre pas dans le champ de la source de cette section."}
      </div>
    </div>
  )
}


/** Bandeau d'historique territorial — commune fusionnée ou amputée (issue #41).

    Rendu `null` quand le périmètre n'a jamais bougé, ce qui est le cas de la quasi-totalité
    des 34 875 communes : afficher un bandeau vide serait du bruit permanent.

    Composant partagé plutôt que trois copies : il doit apparaître sur la synthèse, sur les
    pages thématiques ET sur le PDF imprimé — c'est ce dernier qui circule en réunion, et
    c'est justement celui où le texte rédigé par le LLM se retrouvait sans sa clé de lecture. */
export function BandeauPerimetre({ hist }: { hist?: { resume_detaille: string } | null }) {
  if (!hist?.resume_detaille) return null
  return (
    <div className="obs2-bandeau-perimetre">
      <strong>Périmètre de la commune
        {/* La fiche « geographie-communale » explique la RÈGLE (méthode INSEE, sourcée,
            avec l'addition de Deux-Grosnes en exemple) — demande Marc 30/07 : la
            méthodologie doit être écrite, pas seulement appliquée. */}
        <FicheBtn cle="geographie-communale" /></strong>
      <p>{hist.resume_detaille}</p>
    </div>
  )
}
